import { deleteSourceSelector, updateSourceSelectors } from '../../../../scripts/sourceSelector';
import CheckboxField from '../../fields/CheckBoxField';
import NameField from '../../fields/NameField';
import NumberField from '../../fields/NumberField';
import ProcessorChoiceField from '../../fields/ProcessorChoiceField';
import { DisplayField } from '../../fields/Styles';
import { SourceSelectorIcon } from '../../styling/Icons';
import { getDefaultId } from '../../table/tableUtils';
import { actionTypes } from '../../utils/installationTypes';
import ShowSourceMatrixTabButton from './actions/ShowSourceMatrixTabButton';
import SourceConfigField from './fields/SourceConfigField';
import SourceMatrixChoiceField, { disableSourceMatrixChoiceField, hideSourceMatrixChoiceField } from './fields/SourceMatrixChoiceField';
import SourceSelectorAddressField, { disableSourceSelectorAddressField, hideSourceSelectorAddressField } from './fields/SourceSelectorAddressField';
import SourceSelectorTypeChoiceField from './fields/SourceSelectorTypeChoiceField';
import RoutingModeChoiceField from './fields/RoutingModeChoiceField';
import { addNewSourceSelector, getAvailableSources } from './sourceSelectorUtils';
import { Placeholder } from '../../table/styles/TableStyles';
import AddObjectButton from '../../table/AddObjectButton';
import { InfoIcon } from '../../../UiComponents/Icons';
import theme from '../../../../UI/theme';
import { sourceSelectorTypes } from './sourceSelectorTypes';
import { sourceMatrixTypes } from '../sourceMatrix/sourceMatrixTypes';
import { getRoutingMode, getRoutingModeDisplay, routingModes } from './routingModes';


const idField = 'sourceSelectorId';

// Parse flex outputs which may be stored as a comma-separated string or an array.
const parseFlexOutputsValue = (outputs) => {
    if (Array.isArray(outputs)) return outputs.map(n => parseInt(n)).filter(n => !isNaN(n));
    if (typeof outputs === 'string') {
        return outputs.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n));
    }
    return [];
}

const parseSourceInputsValue = (inputs) => {
    if (Array.isArray(inputs)) return inputs.map(n => parseInt(n)).filter(n => !isNaN(n));
    if (typeof inputs === 'string') {
        return inputs.split(',').map(s => parseInt(s.trim())).filter(n => !isNaN(n));
    }
    return [];
}

export const getSourceSelectorTableProps = ({ sourceSelectors, processors, sources, sourceMatrixes, customerId, setSelectedTab }) => {

    const isFlexMatrixSelector = (sourceSelector) => {
        if (sourceSelector?.type !== sourceSelectorTypes.MATRIX) return false;
        const sm = sourceMatrixes?.find(sm => sm.sourceMatrixId === sourceSelector.sourceMatrixId);
        return sm?.type === sourceMatrixTypes.FLEX;
    }

    const getPairedRoutingValidation = (sourceSelector) => {
        if (!isFlexMatrixSelector(sourceSelector) || getRoutingMode(sourceSelector.routingMode) !== routingModes.PAIRED) return null;

        const outputs = parseFlexOutputsValue(sourceSelector.sourceConfig?.flex?.outputs);
        if (!outputs.length) return null;

        const availableSources = getAvailableSources(
            sourceSelector,
            sources?.filter(source => source.processorId === sourceSelector.processorId) || []
        ) || [];
        const flexSources = availableSources.filter(source => parseSourceInputsValue(source.inputs).length > 0);
        const mismatchedSource = flexSources.find(source => parseSourceInputsValue(source.inputs).length !== outputs.length);

        return mismatchedSource
            ? { type: 'ERROR', message: `Paired routing requires ${outputs.length} input${outputs.length === 1 ? '' : 's'} for each available source. ${mismatchedSource.name || 'A source'} does not match.` }
            : null;
    }

    const getColumns = (sourceSelectors) => [{
        key: 'name',
        displayName: 'Name',
        form: true,
        width: '150px',
        formValidate: (sourceSelector) => {
            return sourceSelector.name ? null : { type: 'ERROR', message: 'Name is required' };
        },
        render: (sourceSelector, updateSourceSelector, cellControl) => (
            <NameField
                object={sourceSelector}
                updateObject={updateSourceSelector}
                objects={sourceSelectors}
                idField={'sourceSelectorId'}
                nameField={'name'}
                cellState={cellControl.cellState}
            />
        )
    }, {
        key: 'processorId',
        displayName: 'Processor',
        width: '150px',
        form: true,
        formValidate: (sourceSelector) => {
            return sourceSelector.processorId ? null : { type: 'ERROR', message: 'Processor is required' };
        },
        getSuggested: () => getDefaultId(processors, 'processorId'),
        render: (sourceSelector, updateSourceSelector, cellControl) => (
            <ProcessorChoiceField
                object={sourceSelector}
                processors={processors}
                updateObject={updateSourceSelector}
                cellControl={cellControl}
            />
        )
    }, {
        key: 'type',
        displayName: 'Type',
        width: '150px',
        form: true,
        render: (sourceSelector, updateSourceSelector, cellControl) => (
            <SourceSelectorTypeChoiceField
                type={sourceSelector.type}
                setValue={(value) => updateSourceSelector({
                    type: value,
                    // MATRIX routes via sourceMatrixId, not address; clear any leftover address on conversion
                    ...(value === sourceSelectorTypes.MATRIX ? { address: null, linkedAddress: null } : {})
                })}
                cellControl={cellControl}
                sourceMatrixes={sourceMatrixes}
                setSelectedTab={setSelectedTab}
            />
        )
    }, {
        key: 'address',
        displayName: <>
            Object Address <InfoIcon
                style={{ fontSize: '16px', color: theme.colors.greenEnergy}}
                onClick={() => {window.open('https://help.waved.co/en/articles/9595553-processor-integrations', "_blank")}}
            />
        </>,
        width: '300px',
        form: true,
        formValidate: (sourceSelector) => {
            if (sourceSelector.type === sourceSelectorTypes.MATRIX) return null;
            return sourceSelector.address ? null : { type: 'ERROR', message: 'Address is required' };
        },
        hideInItem: (sourceSelector) => disableSourceSelectorAddressField(sourceSelector),
        hide: hideSourceSelectorAddressField(sourceSelectors),
        render: (sourceSelector, updateSourceSelector, cellControl) => (
            <SourceSelectorAddressField
                sourceSelector={sourceSelector}
                updateSourceSelector={updateSourceSelector}
                sourceSelectors={sourceSelectors}
                processors={processors}
                cellState={cellControl.cellState}
            />
        )
    }, {
        key: 'useLinkedAddress',
        displayName: 'Linked',
        width: '50px',
        render: (sourceSelector, updateSourceSelector, cellControl) => (
            <CheckboxField
                checked={sourceSelector.useLinkedAddress}
                setValue={() => updateSourceSelector({ useLinkedAddress: +(!sourceSelector.useLinkedAddress) })}
                cellControl={cellControl}
            />
        )
    }, {
        key: 'linkedAddress',
        displayName: 'Linked Address',
        width: '300px',
        form: true,
        hideInItem: (sourceSelector) => !sourceSelector.useLinkedAddress,
        hide: !sourceSelectors?.some(sourceSelector => sourceSelector.useLinkedAddress),
        render: (sourceSelector, updateSourceSelector, cellControl) => (
            <SourceSelectorAddressField
                sourceSelector={sourceSelector}
                updateSourceSelector={updateSourceSelector}
                addressField={'linkedAddress'}
                sourceSelectors={sourceSelectors}
                processors={processors}
                cellState={cellControl.cellState}
            />
        )
    }, {
        key: 'sourceMatrixId',
        displayName: 'Source Matrix',
        width: '300px',
        form: true,
        hideInItem: (sourceSelector) => disableSourceMatrixChoiceField(sourceSelector),
        hide: hideSourceMatrixChoiceField(sourceSelectors),
        render: (sourceSelector, updateSourceSelector, cellControl) => (
            <SourceMatrixChoiceField
                sourceSelector={sourceSelector}
                sourceMatrixes={sourceMatrixes.filter(sourceMatrix => sourceMatrix.processorId === sourceSelector.processorId)}
                updateSourceSelector={updateSourceSelector}
                cellControl={cellControl}
            />
        )
    }, {
        key: 'matrixOutputNumber',
        displayName: 'Output Number',
        width: '100px',
        form: true,
        hide: hideSourceMatrixChoiceField(sourceSelectors),
        hideInItem: (sourceSelector) => disableSourceMatrixChoiceField(sourceSelector) || isFlexMatrixSelector(sourceSelector),
        render: (sourceSelector, updateSourceSelector, cellControl) => (
            <NumberField
                value={sourceSelector.matrixOutputNumber}
                setValue={(value) => updateSourceSelector({ matrixOutputNumber: value })}
                cellState={cellControl.cellState}
                disabled={disableSourceMatrixChoiceField(sourceSelector)}
            />
        )
    }, {
        key: 'routingMode',
        displayName: 'Routing Mode',
        width: '150px',
        form: true,
        hide: !sourceSelectors?.some(sourceSelector => isFlexMatrixSelector(sourceSelector)),
        hideInItem: (sourceSelector) => !isFlexMatrixSelector(sourceSelector),
        formValidate: getPairedRoutingValidation,
        render: (sourceSelector, updateSourceSelector, cellControl) => (
            <RoutingModeChoiceField
                routingMode={sourceSelector.routingMode}
                setValue={(routingMode) => updateSourceSelector({ routingMode: getRoutingMode(routingMode) })}
                cellControl={cellControl}
            />
        )
    }, {
        key: 'mapping',
        displayName: 'Source Config.',
        width: '150px',
        form: true,
        hideInItem: (sourceSelector) => !isFlexMatrixSelector(sourceSelector),
        formValidate: (sourceSelector) => {
            if (!isFlexMatrixSelector(sourceSelector)) return null;
            const outputs = parseFlexOutputsValue(sourceSelector.sourceConfig?.flex?.outputs);
            if (!outputs.length) return { type: 'ERROR', message: 'Flex outputs are required' };
            return getPairedRoutingValidation(sourceSelector);
        },
        render: (sourceSelector, updateSourceSelector, cellControl) => (
            <SourceConfigField
                sourceConfig={sourceSelector.sourceConfig}
                updateSourceConfig={(sourceConfig) => updateSourceSelector({ sourceConfig })}
                sources={sources?.filter(source => source.processorId === sourceSelector.processorId) || []}
                sourceMatrix={sourceMatrixes?.find(sourceMatrix => sourceMatrix.sourceMatrixId === sourceSelector.sourceMatrixId)}
                routingMode={getRoutingMode(sourceSelector.routingMode)}
                routingModeDisplay={getRoutingModeDisplay(sourceSelector.routingMode)}
                cellState={cellControl.cellState}
            />
        )
    }/*, { // Why should a sourceSelector have an orderIndex? was it a mistake and intended for source?
        key: 'orderIndex',
        displayName: 'Order',
        width: '50px',
        render: (object, updateObject, cellControl) => (
            <NumberField
                value={object.orderIndex}
                setValue={(value) => updateObject({ orderIndex: value })}
                cellState={cellControl.cellState}
            />
        )
    }*/]

    const saveObjects = async (changedSourceSelectors) => {
        try {
            await updateSourceSelectors(changedSourceSelectors);
        } catch (err) {
            console.log('Failed to update sourceselectors');
        }
    }

    const deleteObject = async (sourceSelector) => {
        try {
            await deleteSourceSelector(sourceSelector.sourceSelectorId);
        } catch (err) {
            console.log('Error failed to delete sourceSelector');
        }
    }

    const addObject = processors.length ? (newSourceSelector) => {
        newSourceSelector.type = newSourceSelector.type || sourceSelectorTypes.NORMAL;
        newSourceSelector.routingMode = getRoutingMode(newSourceSelector.routingMode);
        addNewSourceSelector(newSourceSelector, sourceSelectors, processors, customerId)
    } : null;

    const getListItemProps = (sourceSelector) => {
        return {
            displayName: sourceSelector.name,
            Icon: SourceSelectorIcon
        }
    }

    const getActions = (sourceSelector) => [{
        key: 'addSourceMatrix',
        type: actionTypes.LIST,
        render: () => (
            <ShowSourceMatrixTabButton setSelectedTab={setSelectedTab} />
        )
    }];

    const getPlaceholder = () => {
        return (
            <Placeholder>
                <p>No source selectors added.</p>
                <AddObjectButton
                    title='source selector' 
                    objects={sourceSelectors}
                    onSubmit={addObject}
                    columns={getColumns(sourceSelectors)}
                    overrideButtonStyle={{
                        title: 'Add source selector',
                    }}
                />
            </Placeholder>
        )
    }

    return {
        title: 'Source selectors',
        getColumns,
        parentObjects: sourceSelectors,
        getListItemProps,
        idField,
        saveObjects,
        deleteObject,
        addObject,
        getActions,
        getPlaceholder
    }
}
